import { useState, useEffect } from 'react';
import { listSourceGeneALL, listSourceVariantAll_VDAs } from '../../search/searchGrid/helpers/tableHelpers';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { trackEnrichmentAction, trackEvent } from '../../../utils/analytics';
import SharedButton from '@/global/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../../hooks/useEnrichmentState';
import { APP_MODAL_Z_INDEX } from '@/constants/modalZIndex';

const EnrichmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialParams,
  searchType,
  notFoundItems,
  user,
  currentText,
}) => {
  const [params, setParams] = useState(initialParams);
  const navigate = useNavigate();
  const { setPendingAdvancedSearchRedirect } = useEnrichmentState();

  const userGroups = user?.['cognito:groups'];
  const isAcademic = Array.isArray(userGroups) && userGroups.includes('ROLE_ACADEMIC');

  useEffect(() => {
    if (isOpen) {
      const adjustedParams = { ...initialParams };

      if (searchType === 'GENES' && adjustedParams.source === 'ALL') {
        adjustedParams.source = 'CURATED';
      }

      if (isAcademic && adjustedParams.source === 'ALL') {
        adjustedParams.source = 'CURATED';
      }

      if (!adjustedParams.commonGenes) {
        adjustedParams.commonGenes = adjustedParams.commonVariants || 2;
      }
      if (!adjustedParams.commonVariants) {
        adjustedParams.commonVariants = adjustedParams.commonGenes || 2;
      }

      setParams(adjustedParams);
    }
  }, [initialParams, isOpen, isAcademic, searchType]);

  // Filter sources based on user role
  const getFilteredSources = () => {
    const sourceList = searchType === 'GENES' ? listSourceGeneALL : listSourceVariantAll_VDAs;

    // If user is academic, only show CURATED
    if (isAcademic) {
      const filtered = {};
      Object.entries(sourceList).forEach(([group, options]) => {
        if (group === 'CURATED') {
          filtered[group] = options;
        }
      });
      return filtered;
    }

    // For other users, show all sources
    return sourceList;
  };

  const filteredSources = getFilteredSources();

  const handleInputChange = (field, value) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    trackEnrichmentAction('run_enrichment_submit', 'enrichment_modal');
    onSubmit(params);
  };

  const handleReset = () => {
    setParams({
      commonVariants: 2,
      commonGenes: 2,
      maxPvalue: 0.05,
      maxScore: 1,
      minScore: 0.1,
      pageNumber: 0,
      source: 'CURATED',
    });
  };

  const handleAdvancedSearchRedirect = () => {
    const unresolvedSymbols = notFoundItems
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 0)
      .join('\n');

    setPendingAdvancedSearchRedirect({
      searchType: 'GENES',
      selectedFilter: 'name',
      text: unresolvedSymbols,
      autoSearch: true,
      originalRequestText: currentText,
      unresolvedItems: notFoundItems,
    });
    navigate('/advancedSearch');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-black bg-opacity-50"
      style={{ zIndex: APP_MODAL_Z_INDEX }}
    >
        <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
          data-demo-tour="enrichment-modal-root"
        >
          <div className="flex-shrink-0 flex justify-between items-center p-6 pb-4">
            <h2 className="text-xl font-bold text-gray-900">Enrichment Parameters</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-demo-tour="enrichment-modal-close"
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              <XMarkIcon className="h-5 w-5 text-black" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
            {notFoundItems.length > 0 && (
              <>
                <div className="warning-message p-2 text-center block w-full max-h-40 overflow-y-auto">
                  <span>
                    These IDs are not found:{' '}
                    <strong className="break-words">{notFoundItems.join(', ')}</strong>
                  </span>
                </div>
                {searchType === 'GENES' && (
                  <p className="mt-2 text-xs text-center">
                    You can find these official gene symbols in{' '}
                    <button
                      type="button"
                      onClick={handleAdvancedSearchRedirect}
                      className="underline font-semibold hover:opacity-80 text-xs text-pink"
                    >
                      Advanced Search
                    </button>
                    .
                  </p>
                )}
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {searchType === 'GENES' ? 'Common Genes' : 'Common Variants'}
                </label>
                <input
                  type="number"
                  value={searchType === 'GENES' ? params.commonGenes : params.commonVariants}
                  onChange={e => {
                    const fieldName = searchType === 'GENES' ? 'commonGenes' : 'commonVariants';
                    handleInputChange(fieldName, parseInt(e.target.value, 10) || 0);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum number of common {searchType === 'GENES' ? 'genes' : 'variants'} required
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max P-value</label>
                <input
                  type="number"
                  step="any"
                  value={params.maxPvalue}
                  onChange={e => handleInputChange('maxPvalue', parseFloat(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="0"
                  max="1"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum p-value threshold (0-1)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Score</label>
                <input
                  type="number"
                  step="0.1"
                  value={params.maxScore}
                  onChange={e => {
                    const raw = parseFloat(e.target.value) ?? 0;
                    const clamped = Math.min(1, Math.max(raw, params.minScore));
                    handleInputChange('maxScore', clamped);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min={params.minScore}
                  max="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum score threshold (0-1, must be ≥ Min Score)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Score</label>
                <input
                  type="number"
                  step="0.1"
                  value={params.minScore}
                  onChange={e => {
                    const raw = parseFloat(e.target.value) ?? 0;
                    const clamped = Math.max(0, Math.min(raw, params.maxScore));
                    handleInputChange('minScore', clamped);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min="0"
                  max={params.maxScore}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum score threshold (0-1, must be ≤ Max Score)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>

                <select
                  value={params.source}
                  onChange={e => handleInputChange('source', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {Object.entries(filteredSources).map(([group, options]) => (
                    <optgroup key={group} label={group}>
                      {(options as string[]).map(opt => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                <p className="text-xs text-gray-500 mt-1">Data source for enrichment analysis</p>
              </div>

              <div className="flex pt-4">
                <SharedButton
                  id="enrichment_run"
                  variant="gradient"
                  type="submit"
                  className="filter-upload"
                  data-demo-tour="enrichment-modal-submit"
                >
                  Run Enrichment
                </SharedButton>
                <SharedButton
                  id="enrichment_reset"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    trackEvent({
                      name: 'form_reset',
                      buttonId: 'enrichment_modal_reset',
                      label: 'Reset enrichment modal',
                      page: 'enrichment',
                      section: 'enrichment_modal',
                      feature: 'enrichment',
                      eventCategory: 'enrichment',
                    });
                    handleReset();
                  }}
                  className="filter-upload"
                >
                  Reset
                </SharedButton>
                <SharedButton
                  id="enrichment_cancel"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    trackEvent({
                      name: 'modal_close',
                      buttonId: 'enrichment_modal_cancel',
                      label: 'Cancel enrichment modal',
                      page: 'enrichment',
                      section: 'enrichment_modal',
                      feature: 'enrichment',
                      eventCategory: 'enrichment',
                    });
                    onClose();
                  }}
                  className="filter-upload"
                >
                  Cancel
                </SharedButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrichmentModal;
